import { useState, useEffect, useCallback } from 'react'
import { useParams, Link } from 'react-router-dom'
import { usePolicy } from '../../../contexts/PolicyContext'
import { useAssessment } from '../../../contexts/AssessmentContext'
import { enrichStructuredPolicy } from '../../../shared/utils/policyAI'
import { exportPolicyToPdf } from '../../../shared/utils/policyExport'
import { exportPolicyAsDocx, exportStructuredPolicyToPdf } from '../utils/policyExport'
import { supabase } from '../../../shared/lib/supabase'
import { useOrg } from '../../../contexts/OrgContext'
import { useDemoPlan } from '../../../hooks/useDemoPlan'
import PolicyPreview from '../components/PolicyPreview'
import GlobalTemplatePolicyPreview from '../../global-admin/components/GlobalTemplatePolicyPreview'
function relativeTime(dateStr) {
  const now = new Date()
  const date = new Date(dateStr)
  const diffMs = now - date
  const diffMin = Math.floor(diffMs / 60000)
  const diffHr = Math.floor(diffMs / 3600000)
  const diffDay = Math.floor(diffMs / 86400000)

  if (diffMin < 1) return 'just now'
  if (diffMin < 60) return `${diffMin}m ago`
  if (diffHr < 24) return `${diffHr}h ago`
  if (diffDay < 30) return `${diffDay}d ago`
  return date.toLocaleDateString()
}

function ClientPoliciesAdmin({ clientId: propClientId, embedded = false } = {}) {
  const params = useParams()
  const clientId = propClientId || params.clientId
  const { clients } = useAssessment()
  const {
    templates,
    clientPolicies,
    fetchClientPolicies,
    updateClientPolicy,
    deleteClientPolicy,
    publishClientPolicy,
    unpublishClientPolicy,
    togglePortalVisibility,
    copyMultipleToClient,
    policyVersions,
    fetchPolicyVersions,
    restoreVersion
  } = usePolicy()

  const { currentOrg } = useOrg() || {}
  const { isDemoOrg, demoMessage } = useDemoPlan()
  const client = clients.find(c => c.id === clientId)
  const policies = clientPolicies[clientId] || []

  // Export loading per-policy
  const [exportingId, setExportingId] = useState(null)
  const [demoNotice, setDemoNotice] = useState('')
  const [orgBrandConfig, setOrgBrandConfig] = useState(null)

  // Modal state
  const [showCopyModal, setShowCopyModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [editingPolicy, setEditingPolicy] = useState(null)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null)
  const [showVersionHistory, setShowVersionHistory] = useState(false)
  const [viewingVersionContent, setViewingVersionContent] = useState(null)
  const [successMessage, setSuccessMessage] = useState('')

  // Copy modal state
  const [selectedTemplates, setSelectedTemplates] = useState([])
  const [copying, setCopying] = useState(false)

  // Edit form state
  const [formTitle, setFormTitle] = useState('')
  const [formContent, setFormContent] = useState('')
  const [formStatus, setFormStatus] = useState('draft')
  const [changeDescription, setChangeDescription] = useState('')
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState('')
  const [viewMode, setViewMode] = useState('preview')
  const [showExportMenu, setShowExportMenu] = useState(false)
  const [showPreviewModal, setShowPreviewModal] = useState(false)
  const [previewPolicy, setPreviewPolicy] = useState(null)
  const [showPreviewExportMenu, setShowPreviewExportMenu] = useState(false)

  // Enrichment state
  const [clientTechnologies, setClientTechnologies] = useState([])
  const [showEnrichConfirm, setShowEnrichConfirm] = useState(null) // policy to enrich

  // Tier 3 enrichment state
  const [enriching, setEnriching] = useState(null) // policy id being enriched
  const [enrichError, setEnrichError] = useState('')
  const [showGapReport, setShowGapReport] = useState(null) // policy object with gap_report

  // Enrichment polling
  const [pollingActive, setPollingActive] = useState(false)

  // Fetch client policies on mount
  useEffect(() => {
    if (clientId) {
      fetchClientPolicies(clientId).catch(console.error)
    }
  }, [clientId, fetchClientPolicies])

  // Start/stop polling based on pending enrichments
  useEffect(() => {
    const hasPending = policies.some(p => p.enrichment_status === 'pending')
    if (hasPending && !pollingActive) setPollingActive(true)
    if (!hasPending && pollingActive) setPollingActive(false)
  }, [policies])

  useEffect(() => {
    if (!pollingActive || !clientId) return
    const interval = setInterval(() => {
      fetchClientPolicies(clientId).catch(console.error)
    }, 4000)
    return () => clearInterval(interval)
  }, [pollingActive, clientId, fetchClientPolicies])

  // Fetch org brand config
  useEffect(() => {
    if (!currentOrg?.id) return
    supabase.from('org_brand_config').select('*').eq('org_id', currentOrg.id).maybeSingle()
      .then(({ data }) => { if (data) setOrgBrandConfig(data) })
  }, [currentOrg?.id])

  // Fetch client technology stack for AI context
  useEffect(() => {
    if (!clientId) return
    const fetchTech = async () => {
      const { data } = await supabase
        .from('client_technology_stack')
        .select('name')
        .eq('client_id', clientId)
      setClientTechnologies((data || []).map(t => t.name))
    }
    fetchTech()
  }, [clientId])

  // Auto-clear success messages
  useEffect(() => {
    if (successMessage) {
      const t = setTimeout(() => setSuccessMessage(''), 3000)
      return () => clearTimeout(t)
    }
  }, [successMessage])

  const openEditModal = useCallback((policy) => {
    setEditingPolicy(policy)
    setFormTitle(policy.title)
    setFormContent(policy.content || '')
    setFormStatus(policy.status)
    setChangeDescription('')
    setSaveError('')
    setShowVersionHistory(false)
    setViewingVersionContent(null)
    setViewMode(policy.content ? 'preview' : 'edit')
    setShowExportMenu(false)
    setShowEditModal(true)
    fetchPolicyVersions(policy.id).catch(console.error)
  }, [fetchPolicyVersions])

  const closeEditModal = () => {
    setShowEditModal(false)
    setEditingPolicy(null)
    setShowVersionHistory(false)
    setViewingVersionContent(null)
    setShowExportMenu(false)
  }

  const handleSave = async (e) => {
    e.preventDefault()
    setSaving(true)
    setSaveError('')
    try {
      await updateClientPolicy(editingPolicy.id, {
        title: formTitle,
        content: formContent
      }, changeDescription || undefined)
      closeEditModal()
    } catch (err) {
      console.error('Error saving policy:', err)
      setSaveError(err.message || 'Failed to save')
    } finally {
      setSaving(false)
    }
  }

  const handlePublish = async (policyId) => {
    try {
      await publishClientPolicy(policyId)
      if (editingPolicy?.id === policyId) {
        setFormStatus('published')
        setEditingPolicy(prev => ({ ...prev, status: 'published' }))
      }
    } catch (err) {
      console.error('Error publishing:', err)
    }
  }

  const handleUnpublish = async (policyId) => {
    try {
      await unpublishClientPolicy(policyId)
      if (editingPolicy?.id === policyId) {
        setFormStatus('draft')
        setEditingPolicy(prev => ({ ...prev, status: 'draft' }))
      }
    } catch (err) {
      console.error('Error unpublishing:', err)
    }
  }

  const handleDelete = async (id) => {
    try {
      await deleteClientPolicy(id)
      setShowDeleteConfirm(null)
    } catch (err) {
      console.error('Error deleting policy:', err)
    }
  }

  const handleCopySelected = async () => {
    if (selectedTemplates.length === 0) return
    setCopying(true)
    try {
      const count = await copyMultipleToClient(selectedTemplates, clientId)
      setShowCopyModal(false)
      setSelectedTemplates([])
      setSuccessMessage(`${count} polic${count !== 1 ? 'ies' : 'y'} copied successfully`)
    } catch (err) {
      console.error('Error copying templates:', err)
    } finally {
      setCopying(false)
    }
  }

  const handleRestoreVersion = async (versionId) => {
    try {
      await restoreVersion(editingPolicy.id, versionId)
      // Refresh the editing policy
      const refreshed = (clientPolicies[clientId] || []).find(p => p.id === editingPolicy.id)
      if (refreshed) {
        setFormContent(refreshed.content)
        setEditingPolicy(refreshed)
      }
      setViewingVersionContent(null)
      setSuccessMessage('Version restored')
    } catch (err) {
      console.error('Error restoring version:', err)
    }
  }

  const toggleTemplateSelection = (templateId) => {
    setSelectedTemplates(prev =>
      prev.includes(templateId) ? prev.filter(id => id !== templateId) : [...prev, templateId]
    )
  }

  const buildClientContext = () => {
    if (!client) return null

    // Get the latest completed assessment scores
    const completedAssessments = (client.assessments || [])
      .filter(a => a.status === 'completed')
      .sort((a, b) => new Date(b.completedAt) - new Date(a.completedAt))

    const latestScores = completedAssessments[0]?.scores || null

    return {
      clientName: client.name,
      industry: client.industry || undefined,
      industrySubCategory: client.industrySubCategory || undefined,
      staffingSize: client.staffingSize || undefined,
      annualRevenue: client.annualRevenue || undefined,
      itTeamSize: client.itTeamSize || undefined,
      geographicalPresence: client.geographicalPresence || undefined,
      technologies: clientTechnologies.length > 0 ? clientTechnologies : undefined,
      maturityLevel: latestScores?.overall?.maturityLevel?.label || undefined,
      overallScore: latestScores?.overall?.score ?? undefined,
      riskAppetite: latestScores?.riskAppetite?.averageAppetite != null
        ? (() => {
            const score = latestScores.riskAppetite.averageAppetite
            if (score <= 2) return 'Conservative'
            if (score <= 3) return 'Moderate'
            if (score <= 4) return 'Aggressive'
            return 'Very Aggressive'
          })()
        : undefined
    }
  }

  const handleEnrich = async (policy) => {
    if (!policy.structured_content) return
    setEnriching(policy.id)
    setEnrichError('')
    try {
      const clientContext = buildClientContext()
      const frameworkTags = policy.framework_tags || []

      const result = await enrichStructuredPolicy({
        domain: policy.domain,
        structuredContent: policy.structured_content,
        frameworkTags,
        clientContext
      })

      // Save enriched content + gap report back to client_policies
      const { error: updateError } = await supabase
        .from('client_policies')
        .update({
          structured_content: result.structured_content,
          enrichment_status: result.enrichment_status || 'enriched_with_gaps',
          gap_report: result.gap_report || [],
          enriched_at: new Date().toISOString()
        })
        .eq('id', policy.id)

      if (updateError) throw updateError

      await fetchClientPolicies(clientId)
      setSuccessMessage('Policy enriched with client-specific context')

      // Show gap report if there are findings
      if (result.gap_report && result.gap_report.length > 0) {
        const refreshed = (clientPolicies[clientId] || []).find(p => p.id === policy.id)
        setShowGapReport({ ...policy, ...refreshed, gap_report: result.gap_report, enrichment_status: result.enrichment_status })
      }
    } catch (err) {
      console.error('Enrichment error:', err)
      setEnrichError(err.message || 'Enrichment failed')
    } finally {
      setEnriching(null)
    }
  }

  const handleExportDocx = async (policy) => {
    if (isDemoOrg) {
      setDemoNotice(demoMessage('Policy downloads'))
      setTimeout(() => setDemoNotice(''), 8000)
      return
    }
    setExportingId(policy.id)
    try {
      // Fetch versions with creator names
      const { data: versions } = await supabase
        .from('policy_versions')
        .select('*, profiles:created_by(full_name)')
        .eq('client_policy_id', policy.id)
        .order('created_at', { ascending: false })

      const enrichedVersions = (versions || []).map(v => ({
        ...v,
        created_by_name: v.profiles?.full_name || 'SSA Advisory Team'
      }))

      // Fetch frameworks
      const tags = policy.framework_tags || []
      let frameworks = []
      if (tags.length > 0) {
        const { data: fwData } = await supabase
          .from('compliance_frameworks')
          .select('*')
          .in('abbreviation', tags)
        frameworks = fwData || []
      }

      const orgName = currentOrg?.name || orgBrandConfig?.firm_display_name || 'Sidewalk Security Advisors'

      await exportPolicyAsDocx(
        policy,
        enrichedVersions,
        client,
        orgBrandConfig,
        orgName,
        frameworks
      )
      setSuccessMessage('Policy exported as .docx')
    } catch (err) {
      console.error('Export failed:', err)
      setSuccessMessage('')
      setEnrichError('Export failed: ' + (err.message || 'Unknown error'))
    } finally {
      setExportingId(null)
    }
  }

  const retryEnrichment = async (policy) => {
    await supabase.from('client_policies').update({
      enrichment_status: 'pending',
      enrichment_error: null
    }).eq('id', policy.id)
    await fetchClientPolicies(clientId)

    const { runEnrichmentPipeline } = await import('../components/PolicyLibrarySelectorModal')
    runEnrichmentPipeline([policy], client, null)
  }

  const handleAcknowledgeGap = async (policy, gapIndex) => {
    const acknowledged = [...(policy.gap_report_acknowledged || [])]
    if (!acknowledged.includes(gapIndex)) acknowledged.push(gapIndex)

    const { error } = await supabase
      .from('client_policies')
      .update({ gap_report_acknowledged: acknowledged })
      .eq('id', policy.id)

    if (!error) {
      await fetchClientPolicies(clientId)
      // Update the gap report modal state if open
      if (showGapReport?.id === policy.id) {
        setShowGapReport(prev => ({ ...prev, gap_report_acknowledged: acknowledged }))
      }
    }
  }

  // Get template IDs already assigned to this client
  const assignedTemplateIds = policies.map(p => p.template_id).filter(Boolean)

  // Only show published templates in the copy modal
  const publishedTemplates = templates.filter(t => t.status === 'published')

  const versions = editingPolicy ? (policyVersions[editingPolicy.id] || []) : []

  if (!client) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500 dark:text-gray-400">Client not found</p>
        <Link to="/clients" className="text-sm text-primary-600 hover:text-primary-700 dark:text-accent-300 dark:hover:text-accent-200 mt-2 inline-block">
          Back to Clients
        </Link>
      </div>
    )
  }

  return (
    <div>
      {/* Success toast */}
      {successMessage && (
        <div className="fixed top-4 right-4 z-50 bg-green-600 text-white px-4 py-3 rounded-lg shadow-lg flex items-center gap-2">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          {successMessage}
        </div>
      )}

      {demoNotice && (
        <div className="fixed top-4 right-4 z-[60] bg-amber-600 text-white px-5 py-4 rounded-lg shadow-lg flex items-start gap-3 max-w-sm">
          <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4.5c-.77-.833-2.694-.833-3.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
          <div>
            <p className="text-sm font-semibold">Demo Mode</p>
            <p className="text-xs mt-0.5 text-amber-100">{demoNotice}</p>
          </div>
          <button onClick={() => setDemoNotice('')} className="ml-auto flex-shrink-0 text-amber-200 hover:text-white">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      {/* Back link — hidden when embedded */}
      {!embedded && (
        <Link
          to={`/clients/${clientId}?tab=policies`}
          className="inline-flex items-center text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 mb-4"
        >
          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to {client.name}
        </Link>
      )}

      {/* Header — simplified when embedded */}
      <div className="flex items-center justify-between mb-6">
        {!embedded ? (
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{client.name} — Policies</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Manage assigned policies for this client</p>
          </div>
        ) : (
          <div />
        )}
        <div className="flex items-center gap-2">
          <button onClick={() => { setShowCopyModal(true); setSelectedTemplates([]) }} className="btn-primary flex items-center gap-2">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7v8a2 2 0 002 2h6M8 7V5a2 2 0 012-2h4.586a1 1 0 01.707.293l4.414 4.414a1 1 0 01.293.707V15a2 2 0 01-2 2h-2M8 7H6a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2v-2" />
            </svg>
            Copy from Library
          </button>
        </div>
      </div>

      {/* Enrichment progress banner */}
      {pollingActive && (
        <div className="mb-4 flex items-center gap-2 px-4 py-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg text-sm text-blue-700 dark:text-blue-300">
          <span className="animate-spin inline-block w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full flex-shrink-0" />
          <span>Policy enrichment is running in the background. This page will update automatically.</span>
        </div>
      )}

      {/* Policy table */}
      {policies.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-12 text-center">
          <svg className="w-12 h-12 text-gray-400 dark:text-gray-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <p className="text-gray-500 dark:text-gray-400 mb-2">No policies assigned to this client yet.</p>
          <p className="text-sm text-gray-400 dark:text-gray-500">Copy templates from your Policy Library or generate a tailored policy with AI.</p>
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700">
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Title</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Domain</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                  <th className="text-center px-4 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Portal</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Version</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Frameworks</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Updated</th>
                  <th className="text-right px-4 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody>
                {policies.map(policy => (
                  <tr
                    key={policy.id}
                    className="border-t border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-900 dark:text-white">
                          {policy.title}
                        </span>
                        {policy.client_edit_pending_review && (
                          <span className="flex-shrink-0 text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 whitespace-nowrap">Client edits</span>
                        )}
                        {policy.client_edited && !policy.client_edit_pending_review && (
                          <span className="flex-shrink-0 text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400 whitespace-nowrap">Customized</span>
                        )}
                        {policy.enrichment_status === 'pending' && (
                          <span className="inline-flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400 flex-shrink-0">
                            <span className="animate-spin inline-block w-3 h-3 border-2 border-blue-500 border-t-transparent rounded-full" />
                            Enriching...
                          </span>
                        )}
                        {policy.enrichment_status === 'enrichment_failed' && (
                          <button
                            onClick={(e) => { e.stopPropagation(); retryEnrichment(policy) }}
                            className="flex-shrink-0 text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 hover:bg-red-200 dark:hover:bg-red-900/40 transition-colors whitespace-nowrap"
                            title={policy.enrichment_error || 'Enrichment failed'}
                          >
                            Retry enrichment
                          </button>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">{policy.domain}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs font-medium px-2.5 py-0.5 rounded-full ${
                        policy.status === 'published'
                          ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                          : policy.status === 'acknowledged'
                          ? 'bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-400'
                          : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
                      }`}>
                        {policy.status.charAt(0).toUpperCase() + policy.status.slice(1)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => togglePortalVisibility(policy.id, !policy.portal_visible)}
                        className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800 ${
                          policy.portal_visible ? 'bg-green-500' : 'bg-gray-300 dark:bg-gray-600'
                        }`}
                        role="switch"
                        aria-checked={policy.portal_visible || false}
                        title={policy.portal_visible ? 'Visible in client portal — click to hide' : 'Hidden from client portal — click to show'}
                      >
                        <span
                          className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                            policy.portal_visible ? 'translate-x-4' : 'translate-x-0'
                          }`}
                        />
                      </button>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">v{policy.current_version}</td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {(policy.framework_tags || []).slice(0, 2).map(tag => (
                          <span key={tag} className="text-xs px-2 py-0.5 rounded bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300">
                            {tag}
                          </span>
                        ))}
                        {(policy.framework_tags || []).length > 2 && (
                          <span className="text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400">
                            +{policy.framework_tags.length - 2}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-400 dark:text-gray-500">{relativeTime(policy.updated_at)}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        {/* Tier 3 Enrich button — only for policies with structured_content */}
                        {policy.structured_content && (
                          <button
                            onClick={() => setShowEnrichConfirm(policy)}
                            disabled={enriching === policy.id}
                            className={`text-gray-400 hover:text-amber-600 dark:hover:text-amber-400 ${enriching === policy.id ? 'animate-pulse' : ''}`}
                            title={policy.enriched_at ? 'Re-enrich with client context' : 'Enrich with client context'}
                          >
                            {enriching === policy.id ? (
                              <div className="w-4 h-4 animate-spin rounded-full border-2 border-amber-500 border-t-transparent" />
                            ) : (
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" />
                              </svg>
                            )}
                          </button>
                        )}
                        {/* Gap report badge — click to view */}
                        {policy.gap_report && policy.gap_report.length > 0 && (
                          <button
                            onClick={() => setShowGapReport(policy)}
                            className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${
                              policy.enrichment_status === 'enriched_clean'
                                ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
                            }`}
                            title="View gap report"
                          >
                            {policy.gap_report.filter(g => g.severity === 'critical').length > 0
                              ? `${policy.gap_report.filter(g => g.severity === 'critical').length} critical`
                              : `${policy.gap_report.length} gaps`}
                          </button>
                        )}
                        <button
                          onClick={() => { setPreviewPolicy(policy); setShowPreviewModal(true); setShowPreviewExportMenu(false) }}
                          className="text-gray-400 hover:text-primary-600 dark:hover:text-primary-400"
                          title="Preview"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                        </button>
                        {/* Export .docx */}
                        <button
                          onClick={() => handleExportDocx(policy)}
                          disabled={exportingId === policy.id}
                          className="text-gray-400 hover:text-blue-600 dark:hover:text-blue-400"
                          title="Export .docx"
                        >
                          {exportingId === policy.id ? (
                            <div className="w-4 h-4 animate-spin rounded-full border-2 border-blue-500 border-t-transparent" />
                          ) : (
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
                            </svg>
                          )}
                        </button>
                        <button
                          onClick={() => openEditModal(policy)}
                          className="text-gray-400 hover:text-primary-600 dark:hover:text-primary-400"
                          title="Edit"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => setShowDeleteConfirm(policy)}
                          className="text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                          title="Delete"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Copy from Library Modal */}
      {showCopyModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">Copy from Policy Library</h2>
              <button onClick={() => setShowCopyModal(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {publishedTemplates.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-500 dark:text-gray-400 mb-2">No published templates in your library.</p>
                <Link to="/policies" className="text-sm text-primary-600 hover:text-primary-700 dark:text-accent-300 dark:hover:text-accent-200">
                  Go to Policy Library
                </Link>
              </div>
            ) : (
              <>
                <div className="space-y-2 mb-6">
                  {publishedTemplates.map(template => {
                    const alreadyAssigned = assignedTemplateIds.includes(template.id)
                    return (
                      <label
                        key={template.id}
                        className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${
                          alreadyAssigned
                            ? 'border-gray-100 dark:border-gray-700/50 opacity-50 cursor-not-allowed'
                            : selectedTemplates.includes(template.id)
                            ? 'border-primary-300 dark:border-primary-600 bg-primary-50 dark:bg-primary-900/20'
                            : 'border-gray-200 dark:border-gray-700 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={selectedTemplates.includes(template.id)}
                          onChange={() => toggleTemplateSelection(template.id)}
                          disabled={alreadyAssigned}
                          className="rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-gray-900 dark:text-white truncate">{template.title}</span>
                            {alreadyAssigned && (
                              <span className="text-xs text-gray-400 dark:text-gray-500 flex-shrink-0">Already assigned</span>
                            )}
                          </div>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{template.domain}</p>
                          {(template.framework_tags || []).length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-1">
                              {template.framework_tags.slice(0, 3).map(tag => (
                                <span key={tag} className="text-xs px-1.5 py-0.5 rounded bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300">
                                  {tag}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </label>
                    )
                  })}
                </div>

                <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <button onClick={() => setShowCopyModal(false)} className="btn-secondary">Cancel</button>
                  <button
                    onClick={handleCopySelected}
                    disabled={copying || selectedTemplates.length === 0}
                    className="btn-primary"
                  >
                    {copying ? 'Copying...' : `Copy ${selectedTemplates.length} Selected`}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && editingPolicy && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">Edit Policy</h2>
              <button onClick={closeEditModal} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Tab toggle: Edit / Preview / Version History */}
            <div className="flex gap-1 mb-6 bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
              <button
                onClick={() => { setShowVersionHistory(false); setViewingVersionContent(null); setViewMode('edit') }}
                className={`flex-1 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  !showVersionHistory && viewMode === 'edit'
                    ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                }`}
              >
                Edit
              </button>
              <button
                onClick={() => { setShowVersionHistory(false); setViewingVersionContent(null); setViewMode('preview') }}
                className={`flex-1 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  !showVersionHistory && viewMode === 'preview'
                    ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                }`}
              >
                Preview
              </button>
              <button
                onClick={() => setShowVersionHistory(true)}
                className={`flex-1 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  showVersionHistory
                    ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                }`}
              >
                History ({versions.length})
              </button>
            </div>

            {!showVersionHistory ? (
              <form onSubmit={handleSave} className="space-y-4">
                {saveError && (
                  <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400 text-sm">
                    {saveError}
                  </div>
                )}

                {/* Title */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Title</label>
                  <input
                    type="text"
                    value={formTitle}
                    onChange={e => setFormTitle(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    required
                  />
                </div>

                {/* Domain (read-only) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Domain</label>
                  <input
                    type="text"
                    value={editingPolicy.domain}
                    disabled
                    className="w-full px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed"
                  />
                </div>

                {/* Content — Edit or Preview based on viewMode */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Content</label>
                    {formContent && (
                      <div className="relative">
                        <button
                          type="button"
                          onClick={() => setShowExportMenu(!showExportMenu)}
                          className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                        >
                          <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          Export
                          <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>
                        {showExportMenu && (
                          <div className="absolute right-0 mt-1 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1 z-10">
                            <button
                              type="button"
                              onClick={async () => {
                                setShowExportMenu(false)
                                if (isDemoOrg) { setDemoNotice(demoMessage('Policy downloads')); setTimeout(() => setDemoNotice(''), 8000); return }
                                try {
                                  await exportPolicyAsDocx(
                                    { ...editingPolicy, title: formTitle || editingPolicy?.domain || 'Policy', content: formContent },
                                    [], client, null, currentOrg?.name || 'Organization'
                                  )
                                } catch (err) {
                                  console.error('DOCX export failed:', err)
                                }
                              }}
                              className="w-full flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                            >
                              <svg className="w-4 h-4 mr-3 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                              Export as .docx
                            </button>
                            <button
                              type="button"
                              onClick={async () => {
                                setShowExportMenu(false)
                                if (isDemoOrg) { setDemoNotice(demoMessage('Policy downloads')); setTimeout(() => setDemoNotice(''), 8000); return }
                                setViewMode('preview')
                                setTimeout(async () => {
                                  try {
                                    if (editingPolicy?.structured_content) {
                                      await exportStructuredPolicyToPdf(editingPolicy, [], client, null, currentOrg?.name)
                                    } else {
                                      await exportPolicyToPdf({ title: formTitle || editingPolicy?.domain || 'Policy', elementId: 'policy-preview-export' })
                                    }
                                  } catch (err) {
                                    console.error('PDF export failed:', err)
                                  }
                                }, 200)
                              }}
                              className="w-full flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                            >
                              <svg className="w-4 h-4 mr-3 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                              </svg>
                              Export as .pdf
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  {viewMode === 'edit' ? (
                    <textarea
                      value={formContent}
                      onChange={e => setFormContent(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white text-gray-900 focus:ring-2 focus:ring-primary-500 focus:border-transparent font-mono text-sm"
                      rows={12}
                      style={{ minHeight: '300px' }}
                    />
                  ) : (
                    <div className="border border-gray-200 rounded-lg overflow-auto" style={{ maxHeight: '500px' }}>
                      <PolicyPreview content={formContent} id="policy-preview-export" />
                    </div>
                  )}
                </div>

                {/* Framework tags (read-only display) */}
                {(editingPolicy.framework_tags || []).length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Framework Tags</label>
                    <div className="flex flex-wrap gap-1">
                      {editingPolicy.framework_tags.map(tag => (
                        <span key={tag} className="text-xs px-2 py-0.5 rounded bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Status + Publish/Unpublish */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Status:</label>
                    <span className={`text-xs font-medium px-2.5 py-0.5 rounded-full ${
                      formStatus === 'published'
                        ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                        : formStatus === 'acknowledged'
                        ? 'bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-400'
                        : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
                    }`}>
                      {formStatus.charAt(0).toUpperCase() + formStatus.slice(1)}
                    </span>
                  </div>
                  {formStatus === 'draft' ? (
                    <button
                      type="button"
                      onClick={() => handlePublish(editingPolicy.id)}
                      className="text-sm text-green-600 hover:text-green-700 dark:text-green-400 dark:hover:text-green-300 flex items-center gap-1"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Publish
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => handleUnpublish(editingPolicy.id)}
                      className="text-sm text-yellow-600 hover:text-yellow-700 dark:text-yellow-400 dark:hover:text-yellow-300 flex items-center gap-1"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                      </svg>
                      Unpublish
                    </button>
                  )}
                </div>

                {/* Client Portal Visibility */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Client Portal:</label>
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {editingPolicy.portal_visible ? 'Visible to client' : 'Hidden from client'}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={async () => {
                      await togglePortalVisibility(editingPolicy.id, !editingPolicy.portal_visible)
                      setEditingPolicy(prev => ({ ...prev, portal_visible: !prev.portal_visible }))
                    }}
                    className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800 ${
                      editingPolicy.portal_visible ? 'bg-green-500' : 'bg-gray-300 dark:bg-gray-600'
                    }`}
                    role="switch"
                    aria-checked={editingPolicy.portal_visible || false}
                  >
                    <span
                      className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                        editingPolicy.portal_visible ? 'translate-x-4' : 'translate-x-0'
                      }`}
                    />
                  </button>
                </div>

                {/* Change description (optional) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Change Description (optional)</label>
                  <input
                    type="text"
                    value={changeDescription}
                    onChange={e => setChangeDescription(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                    placeholder="Describe what changed..."
                  />
                </div>

                {/* Footer buttons */}
                <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <button type="button" onClick={closeEditModal} className="btn-secondary">Cancel</button>
                  <button type="submit" disabled={saving} className="btn-primary">
                    {saving ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </form>
            ) : (
              /* Version History Tab */
              <div>
                {viewingVersionContent ? (
                  <div>
                    <button
                      onClick={() => setViewingVersionContent(null)}
                      className="inline-flex items-center text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 mb-4"
                    >
                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                      Back to versions
                    </button>
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                        Version {viewingVersionContent.version_number}
                      </h3>
                      <button
                        onClick={() => handleRestoreVersion(viewingVersionContent.id)}
                        className="btn-secondary text-sm"
                      >
                        Restore this version
                      </button>
                    </div>
                    <pre className="w-full p-4 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white text-sm font-mono whitespace-pre-wrap overflow-auto max-h-[50vh]">
                      {viewingVersionContent.content}
                    </pre>
                  </div>
                ) : versions.length === 0 ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-8">No version history available.</p>
                ) : (
                  <div className="space-y-2">
                    {versions.map(v => (
                      <div
                        key={v.id}
                        className="flex items-center justify-between p-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                      >
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-gray-900 dark:text-white">v{v.version_number}</span>
                            {v.version_number === editingPolicy.current_version && (
                              <span className="text-xs px-1.5 py-0.5 rounded bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">Current</span>
                            )}
                            {v.edit_source && v.edit_source !== 'admin' && (
                              <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${
                                v.edit_source === 'org_copy' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                                : v.edit_source === 'enrichment' ? 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400'
                                : v.edit_source === 'client_inline' || v.edit_source === 'client_upload' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
                                : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                              }`}>
                                {v.edit_source === 'org_copy' ? 'SSA' : v.edit_source === 'enrichment' ? 'Enrichment' : v.edit_source.startsWith('client') ? 'Client' : v.edit_source}
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                            {v.change_summary || 'No description'}
                          </p>
                          <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                            {new Date(v.created_at).toLocaleString()}
                          </p>
                        </div>
                        <button
                          onClick={() => setViewingVersionContent(v)}
                          className="text-sm text-primary-600 hover:text-primary-700 dark:text-accent-300 dark:hover:text-accent-200"
                        >
                          View
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {showPreviewModal && previewPolicy && (
        <GlobalTemplatePolicyPreview
          template={previewPolicy}
          versionHistory={[]}
          onClose={() => { setShowPreviewModal(false); setPreviewPolicy(null); setShowPreviewExportMenu(false) }}
          onDownloadDocx={async () => {
            if (isDemoOrg) { setDemoNotice(demoMessage('Policy downloads')); setTimeout(() => setDemoNotice(''), 8000); return }
            try {
              await exportPolicyAsDocx(previewPolicy, [], client, null, currentOrg?.name || 'Organization')
            } catch (err) { console.error('DOCX export failed:', err) }
          }}
          onDownloadPdf={async () => {
            if (isDemoOrg) { setDemoNotice(demoMessage('Policy downloads')); setTimeout(() => setDemoNotice(''), 8000); return }
            try {
              if (previewPolicy.structured_content) {
                await exportStructuredPolicyToPdf(previewPolicy, [], client, null, currentOrg?.name)
              } else {
                await exportPolicyToPdf({ title: previewPolicy.title || previewPolicy.domain || 'Policy', elementId: 'global-template-preview-content' })
              }
            } catch (err) { console.error('PDF export failed:', err) }
          }}
        />
      )}

      {/* Enrichment error banner */}
      {enrichError && (
        <div className="fixed top-4 right-4 z-50 bg-red-600 text-white px-4 py-3 rounded-lg shadow-lg flex items-center gap-2 max-w-md">
          <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span className="text-sm">{enrichError}</span>
          <button onClick={() => setEnrichError('')} className="ml-2 text-white/80 hover:text-white">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      {/* Enrich Confirmation Modal */}
      {showEnrichConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 w-full max-w-lg">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Enrich with Client Context</h3>
              <button onClick={() => setShowEnrichConfirm(null)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              The following client context will be sent to Claude Sonnet to tailor <span className="font-medium text-gray-700 dark:text-gray-300">{showEnrichConfirm.title}</span> for this client and generate a gap report.
            </p>

            <div className="p-3 rounded-lg bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 text-sm text-gray-600 dark:text-gray-400 space-y-1.5 mb-4">
              <div><span className="font-medium text-gray-700 dark:text-gray-300">Organization:</span> {client?.name || 'N/A'}</div>
              {client?.industry && <div><span className="font-medium text-gray-700 dark:text-gray-300">Industry:</span> {client.industry}{client.industry_sub_category ? ` \u2014 ${client.industry_sub_category}` : ''}</div>}
              {client?.staffing_size && <div><span className="font-medium text-gray-700 dark:text-gray-300">Size:</span> {client.staffing_size} employees</div>}
              {client?.annual_revenue && <div><span className="font-medium text-gray-700 dark:text-gray-300">Revenue:</span> {client.annual_revenue}</div>}
              {client?.geographical_presence && <div><span className="font-medium text-gray-700 dark:text-gray-300">Geography:</span> {client.geographical_presence}</div>}
              {clientTechnologies.length > 0 && (
                <div><span className="font-medium text-gray-700 dark:text-gray-300">Security Tools:</span> {clientTechnologies.slice(0, 5).join(', ')}{clientTechnologies.length > 5 ? ` +${clientTechnologies.length - 5} more` : ''}</div>
              )}
              {(() => {
                const ctx = buildClientContext()
                return ctx?.maturityLevel ? <div><span className="font-medium text-gray-700 dark:text-gray-300">Maturity:</span> {ctx.maturityLevel}{ctx.overallScore != null ? ` (${ctx.overallScore}/100)` : ''}</div> : null
              })()}
              {(() => {
                const ctx = buildClientContext()
                return ctx?.riskAppetite ? <div><span className="font-medium text-gray-700 dark:text-gray-300">Risk Appetite:</span> {ctx.riskAppetite}</div> : null
              })()}
              {(showEnrichConfirm.framework_tags || []).length > 0 && (
                <div className="pt-1.5 border-t border-gray-200 dark:border-gray-600 mt-1.5">
                  <span className="font-medium text-gray-700 dark:text-gray-300">Frameworks:</span>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {showEnrichConfirm.framework_tags.map(tag => (
                      <span key={tag} className="text-xs px-2 py-0.5 rounded bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300">{tag}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <p className="text-xs text-gray-400 dark:text-gray-500 mb-4">This context is sent to the AI to tailor the policy and identify compliance gaps for this client.</p>

            <div className="flex justify-end gap-3">
              <button onClick={() => setShowEnrichConfirm(null)} className="btn-secondary">Cancel</button>
              <button
                onClick={() => { const policy = showEnrichConfirm; setShowEnrichConfirm(null); handleEnrich(policy) }}
                disabled={enriching === showEnrichConfirm.id}
                className="btn-primary flex items-center gap-2"
              >
                {enriching === showEnrichConfirm.id ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                    Enriching...
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" />
                    </svg>
                    Enrich Policy
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Gap Report Modal */}
      {showGapReport && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
              <div>
                <h2 className="text-lg font-bold text-gray-900 dark:text-white">Gap Report</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                  {showGapReport.title} — {showGapReport.domain}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${
                  showGapReport.enrichment_status === 'enriched_clean'
                    ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                    : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
                }`}>
                  {showGapReport.enrichment_status === 'enriched_clean' ? 'Clean' : 'Gaps Found'}
                </span>
                <button
                  onClick={() => setShowGapReport(null)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-4">
              {/* Summary counts */}
              <div className="grid grid-cols-3 gap-3 mb-6">
                {['critical', 'warning', 'info'].map(severity => {
                  const count = (showGapReport.gap_report || []).filter(g => g.severity === severity).length
                  const styles = {
                    critical: 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-red-700 dark:text-red-400',
                    warning: 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800 text-amber-700 dark:text-amber-400',
                    info: 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-400'
                  }
                  return (
                    <div key={severity} className={`rounded-lg border p-3 text-center ${styles[severity]}`}>
                      <div className="text-2xl font-bold">{count}</div>
                      <div className="text-xs font-medium capitalize">{severity}</div>
                    </div>
                  )
                })}
              </div>

              {/* Gap items */}
              <div className="space-y-3">
                {(showGapReport.gap_report || []).map((gap, idx) => {
                  const severityStyles = {
                    critical: 'border-l-red-500 bg-red-50/50 dark:bg-red-900/10',
                    warning: 'border-l-amber-500 bg-amber-50/50 dark:bg-amber-900/10',
                    info: 'border-l-blue-500 bg-blue-50/50 dark:bg-blue-900/10'
                  }
                  const badgeStyles = {
                    critical: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
                    warning: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
                    info: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                  }
                  return (
                    <div
                      key={idx}
                      className={`border-l-4 rounded-r-lg p-4 ${severityStyles[gap.severity] || severityStyles.info}`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${badgeStyles[gap.severity] || badgeStyles.info}`}>
                              {gap.severity}
                            </span>
                            {gap.framework && (
                              <span className="text-xs font-medium px-2 py-0.5 rounded bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400">
                                {gap.framework}
                              </span>
                            )}
                          </div>
                          <h4 className="text-sm font-medium text-gray-900 dark:text-white">{gap.section}</h4>
                          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{gap.gap_description}</p>
                        </div>
                        {!(showGapReport.gap_report_acknowledged || []).includes(idx) && (
                          <button
                            onClick={() => handleAcknowledgeGap(showGapReport, idx)}
                            className="flex-shrink-0 text-xs px-2.5 py-1 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                          >
                            Acknowledge
                          </button>
                        )}
                        {(showGapReport.gap_report_acknowledged || []).includes(idx) && (
                          <span className="flex-shrink-0 text-xs text-green-600 dark:text-green-400 flex items-center gap-1">
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            Done
                          </span>
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>

            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex-shrink-0">
              <div className="flex justify-end">
                <button onClick={() => setShowGapReport(null)} className="btn-secondary">Close</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 w-full max-w-sm">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Remove Policy</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
              Are you sure you want to remove <span className="font-medium">&ldquo;{showDeleteConfirm.title}&rdquo;</span> from this client? The master template is not affected. This cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setShowDeleteConfirm(null)} className="btn-secondary">Cancel</button>
              <button
                onClick={() => handleDelete(showDeleteConfirm.id)}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ClientPoliciesAdmin
